/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { ProductSearchResponse } from '@/plp/data/models/api';
import {
  SearchFilter,
  SearchInput,
  SearchInputUnit,
  SearchSort,
  SearchResults,
  SearchResultUnit,
  SearchResultSuggestion,
  SearchFacetContext,
  SearchBucket,
  SearchResultProduct,
} from '@/plp/data/models/acdl';

const SEARCH_INPUT_CONTEXT = 'searchInputContext';
const SEARCH_RESULTS_CONTEXT = 'searchResultsContext';
const PLP_UNIT_ID = 'livesearch-plp';
const SEARCH_UNIT_ID = 'livesearch-popover';
//const SEARCH_CATEGORY_CLICK = 'search-category-click';
const SEARCH_PRODUCT_CLICK = 'search-product-click';
const SEARCH_REQUEST_SENT = 'search-request-sent';
const SEARCH_RESPONSE_RECEIVED = 'search-response-received';
const SEARCH_RESULTS_VIEW = 'search-results-view';
const CATEGORY_RESULTS_VIEW = 'category-results-view';
const CHANNEL_CONTEXT = 'channelContext';

function getAdobeDataLayer() {
  // @ts-ignore
  window.adobeDataLayer = window.adobeDataLayer || [];
  // @ts-ignore
  return window.adobeDataLayer;
}

// Get a context from ACDL
function getContext<T>(name?: string): T {
  const adobeDataLayer = getAdobeDataLayer();
  return adobeDataLayer.getState ? adobeDataLayer.getState(name) : ({} as T);
}

function setContext(name: string, data: any) {
  const adobeDataLayer = getAdobeDataLayer();

  // Clear existing context
  adobeDataLayer.push({
    [name]: null,
  });

  // Set new context
  adobeDataLayer.push({
    [name]: data,
  });
}

function getChannelContext() {
  return {
    _id: "https://ns.adobe.com/xdm/channels/web",
    _type: "https://ns.adobe.com/xdm/channel-types/web",
  };
}

function setChannelContext() {
  setContext(CHANNEL_CONTEXT, getChannelContext());
}

function pushEvent(event: string, additionalContext?: any) {
  const adobeDataLayer = getAdobeDataLayer();

  adobeDataLayer.push((acdl: any) => {
    const state = acdl.getState ? acdl.getState() : {};

    acdl.push({
      event,
      eventInfo: {
        ...state,
        ...additionalContext,
      },
    });
  });
}

const searchRequestSent = (searchUnitId: string) => {
  setChannelContext();
  pushEvent(SEARCH_REQUEST_SENT, { searchUnitId });
};

const searchResponseReceived = (searchUnitId: string) => {
  setChannelContext();
  pushEvent(SEARCH_RESPONSE_RECEIVED, { searchUnitId });
};

const categoryResultsView = (searchUnitId: string) => {
  pushEvent(CATEGORY_RESULTS_VIEW, { searchUnitId });
};

const searchResultsView = (searchUnitId: string) => {
  pushEvent(SEARCH_RESULTS_VIEW, { searchUnitId });
};

const searchProductClick = (sku: string, searchUnitId: string) => {
  pushEvent(SEARCH_PRODUCT_CLICK, { sku, searchUnitId });
};

const updateSearchInputCtx = (
  searchUnitId: string,
  searchRequestId: string,
  phrase: string,
  filters: Array<SearchFilter>,
  pageSize: number,
  currentPage: number,
  sort: Array<SearchSort>,
): void => {
  const acdl = getAdobeDataLayer;
  if (!acdl) {
    // don't break search if events are broken/not loading
    return;
  }

  const searchInputCtx: SearchInput = getContext(SEARCH_INPUT_CONTEXT) || {};
  if (!searchInputCtx?.units) {
    searchInputCtx.units = [];
  }

  // create search input unit
  const searchInputUnit: SearchInputUnit = {
    searchUnitId,
    searchRequestId,
    queryTypes: ['products', 'suggestions'],
    phrase,
    pageSize,
    currentPage,
    filter: filters,
    sort,
  };

  // find search input unit index
  const searchInputUnitIndex = searchInputCtx.units.findIndex((unit) => unit.searchUnitId === searchUnitId);

  // update search input unit
  if (searchInputUnitIndex < 0) {
    searchInputCtx.units.push(searchInputUnit);
  } else {
    searchInputCtx.units[searchInputUnitIndex] = searchInputUnit;
  }

  setContext(SEARCH_INPUT_CONTEXT, searchInputCtx);
};

const updateSearchResultsCtx = (
  searchUnitId: string,
  searchRequestId: string,
  results: ProductSearchResponse['productSearch'],
): void => {
  const acdl = getAdobeDataLayer;
  if (!acdl) {
    // don't break search if events are broken/not loading
    return;
  }
  const searchResultsCtx: SearchResults = getContext(SEARCH_RESULTS_CONTEXT) || {};
  if (!searchResultsCtx?.units) {
    searchResultsCtx.units = [];
  }

  // find search result unit index
  const searchResultUnitIndex = searchResultsCtx.units.findIndex((unit) => unit.searchUnitId === searchUnitId);

  // create search result unit
  const searchResultUnit: SearchResultUnit = {
    searchUnitId,
    searchRequestId,
    products: createProducts(results.items),
    categories: [],
    suggestions: createSuggestions(results.suggestions),
    page: results?.pageInfo?.currentPage || 1,
    perPage: results?.pageInfo?.pageSize || 20,
    facets: createFacets(results.facets as SearchFacetContext[]),
  };

  // update search result unit
  if (searchResultUnitIndex < 0) {
    searchResultsCtx.units.push(searchResultUnit);
  } else {
    searchResultsCtx.units[searchResultUnitIndex] = searchResultUnit;
  }

  setContext(SEARCH_RESULTS_CONTEXT, searchResultsCtx);
};

const createProducts = (items: ProductSearchResponse['productSearch']['items']): SearchResultProduct[] => {
  if (!items) {
    return [];
  }

  const products: SearchResultProduct[] = items.map((item, index) => ({
    name: item?.name,
    sku: item?.sku,
    url: item?.url ?? '',
    imageUrl: item?.images[0]?.url ?? '',
    price: item?.price?.final?.amount?.value ?? item?.priceRange?.minimum?.regular?.amount?.value,
    rank: index,
  }));

  return products;
};

const createSuggestions = (items: ProductSearchResponse['productSearch']['suggestions']): SearchResultSuggestion[] => {
  if (!items) {
    return [];
  }

  const suggestions: SearchResultSuggestion[] = items.map((suggestion, index) => ({
    suggestion,
    rank: index,
  }));

  return suggestions;
};

const createFacets = (items: SearchFacetContext[]): SearchFacetContext[] => {
  if (!items) {
    return [];
  }

  const facets = items.map<SearchFacetContext>((item) => ({
    attribute: item?.attribute,
    title: item?.title,
    type: item?.type || 'PINNED',
    buckets: item?.buckets.map<SearchBucket>((bucket: any) => bucket),
  }));

  return facets;
};

export {
  searchRequestSent,
  searchResponseReceived,
  categoryResultsView,
  searchResultsView,
  searchProductClick,
  updateSearchInputCtx,
  updateSearchResultsCtx,
  PLP_UNIT_ID,
  SEARCH_UNIT_ID,
};
